import ContentClipFade from "@components/shared/ContentClipFade/ContentClipFade";
import { FEATURE_FLAGS } from "@constants";
import { RootState } from "@redux/store";
import { MutableRefObject, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

const defaultTopOffset = 20;
/* Avoids overlap from sticky topbar (app/client/src/components/Navbar/Navbar.tsx) */
const topOffsetForTopBar = 112;

// Hook to compute progressive gate state + JSON-LD schema
export function useProgressiveDisclosure(pageTitle?: string) {
  const currentUser = useSelector((state: RootState) => state.profiles.currentUser);

  const isSignedOut = !currentUser.id;
  const shouldGate = FEATURE_FLAGS.RFP_PROGRESSIVE_CONTENT && isSignedOut;

  const [isRightColumnClipped, setIsRightColumnClipped] = useState(false);

  useEffect(() => {
    const handler = (e: any) => {
      const clipped = Boolean(e?.detail?.clipped);
      setIsRightColumnClipped(clipped);
    };
    window.addEventListener("halo:right-column-clipped", handler as any);
    return () => {
      window.removeEventListener("halo:right-column-clipped", handler as any);
    };
  }, []);

  const paywallJsonLd = useMemo(() => {
    if (!shouldGate) return null as any;

    const pageUrl = window.location.href;
    const hasPart: any[] = [
      {
        "@type": "WebPageElement",
        isAccessibleForFree: false,
        cssSelector: ".paywalled-content",
      },
    ];

    if (isRightColumnClipped) {
      hasPart.push({
        "@type": "WebPageElement",
        isAccessibleForFree: false,
        cssSelector: ".right-column-content-clip.is-clipped",
      });
    }

    const schema: any = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      isAccessibleForFree: false,
      hasPart,
    };
    if (pageTitle) schema.name = pageTitle;
    if (pageUrl) schema.url = pageUrl;
    return schema;
  }, [shouldGate, pageTitle, isRightColumnClipped]);

  return {
    shouldGate,
    isSignedOut,
    topOffset: isSignedOut ? topOffsetForTopBar : defaultTopOffset,
    paywallJsonLd,
  } as const;
}

// Shared styled components used to position gate

export const GateContainer = styled.div`
  scroll-margin-top: 100px;
  margin-top: 20px;
`;

// Right column wrapper that fades only the portion below the gate cutoff.
// Disabled on stacked/mobile layouts.
export function RightColumnFade({
  shouldGate,
  gateRef,
  children,
  as,
}: {
  shouldGate: boolean;
  gateRef: MutableRefObject<HTMLElement | null> | { current: HTMLElement | null };
  children: ReactNode;
  as?: any;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [cutoff, setCutoff] = useState<number | null>(null);

  useEffect(() => {
    const containerEl = containerRef.current;
    const gateEl = gateRef?.current ?? null;

    if (!containerEl || !gateEl || !shouldGate) {
      setCutoff(null);
      return;
    }

    const compute = () => {
      const gateRect = gateEl.getBoundingClientRect();
      const rightRect = containerEl.getBoundingClientRect();
      const gatePageTop = gateRect.top + window.scrollY;
      const rightPageTop = rightRect.top + window.scrollY;
      const relativeCut = gatePageTop - rightPageTop; // px from top of right column

      const rightHeight = containerEl.scrollHeight;

      if (relativeCut <= 0) {
        setCutoff(0);
      } else if (relativeCut >= rightHeight) {
        // Gate starts after the sidebar ends: no fade
        setCutoff(null);
      } else {
        setCutoff(Math.max(0, relativeCut - 20));
      }
    };

    compute();

    const roGate = new ResizeObserver(() => compute());
    roGate.observe(gateEl);

    const onResize = () => compute();
    window.addEventListener("resize", onResize);

    return () => {
      roGate.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [shouldGate, gateRef]);

  const effectiveMaxHeight = shouldGate && cutoff !== null ? cutoff : null;
  const isClipped = shouldGate && cutoff !== null;

  useEffect(() => {
    const event = new CustomEvent("halo:right-column-clipped", {
      detail: { clipped: isClipped },
    });
    window.dispatchEvent(event);
  }, [isClipped]);

  return (
    <ContentClipFade
      ref={containerRef}
      as={as}
      maxHeightPx={effectiveMaxHeight}
      disableOnBreakpoints={["tabletLandscape", "mobile"]}
      className={`right-column-content-clip${isClipped ? " is-clipped" : ""}`}
    >
      {children}
    </ContentClipFade>
  );
}
